import type { MessengerBridge, ThemeScheme } from './types'

type TelegramBackButton = {
  show: () => void
  hide: () => void
  onClick: (callback: () => void) => void
  offClick: (callback: () => void) => void
}

type TelegramWebApp = {
  initData: string
  initDataUnsafe: { start_param?: string }
  colorScheme: string
  ready: () => void
  expand: () => void
  close: () => void
  openTelegramLink: (url: string) => void
  openLink?: (url: string, options?: { try_instant_view?: boolean }) => void
  BackButton?: TelegramBackButton
}

const getTelegramWebApp = (): TelegramWebApp | null => {
  const webApp = window.Telegram?.WebApp as TelegramWebApp | undefined
  return webApp ?? null
}

const mapTheme = (colorScheme: string): ThemeScheme =>
  colorScheme === 'dark' ? 'dark' : 'light'

export const isTelegramWebApp = (): boolean => getTelegramWebApp() !== null

export const createTelegramBridge = (): MessengerBridge => {
  const webApp = getTelegramWebApp()
  if (!webApp) {
    throw new Error('Telegram WebApp is not available')
  }

  let backHandler: (() => void) | null = null

  return {
    platform: 'telegram',

    getInitData() {
      return webApp.initData ?? ''
    },

    getStartParam() {
      const fromSdk = webApp.initDataUnsafe?.start_param
      if (fromSdk) {
        return fromSdk
      }
      const query = new URLSearchParams(window.location.search)
      return query.get('tgWebAppStartParam') ?? query.get('startParam')
    },

    getTheme() {
      return mapTheme(webApp.colorScheme ?? 'light')
    },

    ready() {
      webApp.ready()
    },

    expand() {
      webApp.expand()
    },

    close() {
      webApp.close()
    },

    setBackHandler(handler) {
      const backButton = webApp.BackButton
      if (!backButton) {
        return
      }
      if (backHandler) {
        backButton.offClick(backHandler)
        backHandler = null
      }
      if (handler) {
        backHandler = handler
        backButton.onClick(handler)
        backButton.show()
      } else {
        backButton.hide()
      }
    },

    openTelegramLink(url: string) {
      webApp.openTelegramLink(url)
    },

    openLink(url: string) {
      if (typeof webApp.openLink === 'function') {
        webApp.openLink(url)
        return
      }
      window.open(url, '_blank', 'noopener,noreferrer')
    },
  }
}
